import { ChangeEvent, useEffect, useState } from 'react';
import { FormLabel } from '@mui/material';

import { CoreBox, CoreCheckbox, CoreFormControlLabel, CoreTypography } from '@youscience/core';

import useRecommenderStore from '@store/RecommenderStore';
import useRecommenderProfileStore, { initialFormState, SectionName } from '@store/RecommenderProfileStore';

import SectionAccordion from '@components/SectionAccordion';

import { FormState } from '@constants/commonApp/types';

import { SchoolDetailsProps, CertificationsConstants, CertificationsFields } from '../../types';
import { StyledCard } from '@pages/RecommenderSpace/components/Card.styles';

export const Certifications = ({ onExpand, isExtended }: SchoolDetailsProps<CertificationsFields>) => {
  const { recommenderProfile } = useRecommenderStore((store) => store);
  const {
    initialState: { certifications: initialState },
    formState: { certifications: certificationsFormState },
    setInitialData,
    setSectionFormState,
  } = useRecommenderProfileStore((store) => store);

  const [formState, setFormState] = useState<FormState<CertificationsFields>>(certificationsFormState);

  const isShowErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';
  const areAllRequiredFieldsFilled = Boolean(formState.data?.transcript && formState.data?.communication);

  const initializeData = () => {
    const Certifications = recommenderProfile?.counselorProfile?.certifications;

    const initialValues = {
      transcript: Certifications?.transcript,
      communication: Certifications?.communication,
    };

    setInitialData(SectionName.Certifications, initialValues);
    setFormState({
      ...initialFormState,
      data: initialValues,
    });
  };

  useEffect(() => {
    initializeData();
  }, [recommenderProfile]);

  const updateFormValues = (fieldUpdates: Partial<CertificationsFields>) => {
    setFormState((prevState) => {
      const newFormState = {
        ...prevState,
        data: {
          ...prevState.data,
          ...fieldUpdates,
        },
      };

      const fields = Object.keys(newFormState.data) as Array<keyof CertificationsFields>;

      newFormState.hasChanged = fields.some((field) => {
        if (!initialState) {
          return false;
        }

        return newFormState.data[field] !== initialState[field];
      });

      setSectionFormState(SectionName.Certifications, newFormState);
      return newFormState;
    });
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    updateFormValues({ [event.target.name]: checked ? CertificationsConstants[event.target.name] : undefined });
  };

  return (
    <SectionAccordion
      title={'Certifications'}
      onChange={onExpand}
      isExpanded={isExtended}
      isChanged={formState.hasChanged}
      isError={isShowErrors && !areAllRequiredFieldsFilled}
    >
      {Object.keys(formState.data).length === 0 ? null : (
        <StyledCard>
          <CoreBox sx={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
            <CoreTypography variant="subtitle2" component="h4">
              Transcript Affirmation
            </CoreTypography>

            <FormLabel>Please affirm the following before you submit your form. *</FormLabel>
            <CoreFormControlLabel
              name="transcript"
              label={
                <CoreTypography>
                  By checking this box, I certify that I will upload a PDF of each student's transcript for transmission
                  with the School Report. (Parchment users should also check this box.) I understand that Common App
                  member institutions do not want to receive the School Report transmitted online if the transcript will
                  be mailed. I also understand that if I fail to send the transcript online with the School Report this
                  may cause confusion and processing delays, resulting in an late application decision for the applicant
                </CoreTypography>
              }
              control={
                <CoreCheckbox value={true} onChange={handleCheckboxChange} checked={!!formState.data.transcript} />
              }
            />
          </CoreBox>

          <CoreBox sx={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
            <CoreTypography variant="subtitle2" component="h4">
              Communication
            </CoreTypography>
            <FormLabel>Contact Terms *</FormLabel>
            <CoreFormControlLabel
              name="communication"
              label={
                <CoreTypography>
                  I understand that the Common Application organization may need to periodically contact me regarding
                  updates to the online school forms system. I agree to receive such communications at the email address
                  I have provided above.
                </CoreTypography>
              }
              control={
                <CoreCheckbox value={true} onChange={handleCheckboxChange} checked={!!formState.data.communication} />
              }
            />
          </CoreBox>
        </StyledCard>
      )}
    </SectionAccordion>
  );
};
